import React from 'react';

import { ActionIcon, Box, Button, Flex, Group, Paper, ScrollArea, Stack, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { DroxyAccordion } from 'components/Droxy/DroxyAccordion';
import { DroxyAccordionSection } from 'components/Droxy/DroxyAccordionSection';
import { SectionNumber } from 'components/SectionNumber';
import { ToolConfigurationPanelWrapper } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/ToolConfigurationPanelWrapper';
import { ToolStatusSection } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/ToolStatusSection';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { CallTransferToolConfig } from 'shared/interfaces/tools/CallTransfer';
import { ToolConfig, ToolKind, ToolTrigger } from 'shared/interfaces/tools/Tool';
import { getToolConfig } from 'shared/interfaces/tools/utils';
import { formHasFieldErrors } from 'shared/utils/form';
import { MAX_CALL_TRANSFER_RULES, requiredInputValidator, validateIsPhoneNumber } from 'shared/utils/form-validators';

enum CallTransferPanelSections {
  TRANSFER_RULES = 'transfer-rules'
}

interface CallTransferConfigurationPanelProps {
  onUpdate: (toolConfigs: ToolConfig[]) => void;
  onBack: () => void;
}

export function CallTransferConfigurationPanel({ onUpdate, onBack }: CallTransferConfigurationPanelProps) {
  const location = useLocation();
  const toolConfigs: ToolConfig[] = location.state?.toolConfigs ?? [];
  const maybeExistingToolConfig = getToolConfig(toolConfigs, ToolKind.CALL_TRANSFER);
  const isNewToolConfig = !maybeExistingToolConfig;

  const callTransferToolConfigForm = useForm<CallTransferToolConfig>({
    initialValues: {
      _id: maybeExistingToolConfig?._id,
      kind: ToolKind.CALL_TRANSFER as const,
      toolName: ToolKind.CALL_TRANSFER as const,
      trigger: ToolTrigger.INTENT as const,
      enabled: maybeExistingToolConfig?.enabled ?? true,
      callTransferConfigs: maybeExistingToolConfig?.callTransferConfigs || [
        {
          phoneNumber: '',
          transferCondition: ''
        }
      ]
    },
    validate: {
      trigger: (value) => requiredInputValidator(value),
      callTransferConfigs: {
        phoneNumber: (value) => validateIsPhoneNumber(value),
        transferCondition: (value) => requiredInputValidator(value)
      }
    }
  });

  const { t } = useTranslation();

  const handleAddRule = () => {
    const currentLength = callTransferToolConfigForm.values.callTransferConfigs.length;
    if (currentLength < MAX_CALL_TRANSFER_RULES) {
      callTransferToolConfigForm.insertListItem('callTransferConfigs', { phoneNumber: '', transferCondition: '' });
    }
  };

  const handleRemoveRule = (ruleIndex: number) => {
    callTransferToolConfigForm.removeListItem('callTransferConfigs', ruleIndex);
  };

  const handleUpdate = () => {
    const validation = callTransferToolConfigForm.validate();
    if (validation.hasErrors) return;
    onUpdate([callTransferToolConfigForm.values]);
  };

  const renderTransferRule = (ruleIndex: number) => {
    const hasPhoneNumberError = !!callTransferToolConfigForm.errors?.[`callTransferConfigs.${ruleIndex}.phoneNumber`];
    const hasConditionError =
      !!callTransferToolConfigForm.errors?.[`callTransferConfigs.${ruleIndex}.transferCondition`];
    const hasError = hasPhoneNumberError || hasConditionError;
    const canDelete = callTransferToolConfigForm.values.callTransferConfigs.length > 1;

    return (
      <Paper
        key={ruleIndex}
        p="md"
        withBorder
        sx={(theme) => ({
          backgroundColor: hasError ? theme.fn.rgba(theme.colors.red[6], 0.05) : undefined
        })}>
        <Stack spacing="md">
          <Group position="apart" align="center">
            <Title order={4}>{t('SMART_ASSISTANT.TOOLS.CALL_TRANSFER.RULE_TITLE')}</Title>
            {canDelete && (
              <ActionIcon onClick={() => handleRemoveRule(ruleIndex)}>
                <IconTrash size={18} stroke={1.5} />
              </ActionIcon>
            )}
          </Group>

          <Stack spacing="md">
            <TextInput
              label={t('SMART_ASSISTANT.TOOLS.CALL_TRANSFER.PHONE_NUMBER_LABEL')}
              placeholder={t('SMART_ASSISTANT.TOOLS.CALL_TRANSFER.PHONE_NUMBER_PLACEHOLDER')}
              error={callTransferToolConfigForm.errors?.[`callTransferConfigs.${ruleIndex}.phoneNumber`]}
              {...callTransferToolConfigForm.getInputProps(`callTransferConfigs.${ruleIndex}.phoneNumber`)}
            />

            <TextInput
              label={t('SMART_ASSISTANT.TOOLS.CALL_TRANSFER.CONDITION_LABEL')}
              placeholder={t('SMART_ASSISTANT.TOOLS.CALL_TRANSFER.CONDITION_PLACEHOLDER')}
              error={callTransferToolConfigForm.errors?.[`callTransferConfigs.${ruleIndex}.transferCondition`]}
              {...callTransferToolConfigForm.getInputProps(`callTransferConfigs.${ruleIndex}.transferCondition`)}
            />
          </Stack>
        </Stack>
      </Paper>
    );
  };

  return (
    <ToolConfigurationPanelWrapper
      onSave={handleUpdate}
      onBack={onBack}
      onCancel={isNewToolConfig ? onBack : () => callTransferToolConfigForm.reset()}
      hasChanges={callTransferToolConfigForm.isDirty() || isNewToolConfig}
      px={0}>
      <Box>
        <ToolStatusSection
          enabled={callTransferToolConfigForm.values.enabled}
          onToggle={(enabled) => callTransferToolConfigForm.setFieldValue('enabled', enabled)}
        />
      </Box>
      <Box>
        <DroxyAccordion defaultOpenSections={[CallTransferPanelSections.TRANSFER_RULES]}>
          <DroxyAccordionSection
            title={t('SMART_ASSISTANT.TOOLS.CALL_TRANSFER.TRANSFER_RULES')}
            section={CallTransferPanelSections.TRANSFER_RULES}
            icon={<SectionNumber number={1} />}
            hasError={formHasFieldErrors(callTransferToolConfigForm, 'trigger', 'callTransferConfigs')}>
            <Flex direction="column">
              <ScrollArea.Autosize mah={600} type="never">
                <Stack spacing={24}>
                  {callTransferToolConfigForm.values.callTransferConfigs.map((_, index) => renderTransferRule(index))}
                </Stack>
                <Box sx={{ paddingTop: 28 }}>
                  <Button
                    leftIcon={<IconPlus />}
                    onClick={handleAddRule}
                    disabled={callTransferToolConfigForm.values.callTransferConfigs.length >= MAX_CALL_TRANSFER_RULES}
                    fullWidth>
                    {t('SMART_ASSISTANT.TOOLS.CALL_TRANSFER.ADD_RULE')}
                  </Button>
                </Box>
              </ScrollArea.Autosize>
            </Flex>
          </DroxyAccordionSection>
        </DroxyAccordion>
      </Box>
    </ToolConfigurationPanelWrapper>
  );
}
